import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { ArrowRight, Download, RefreshCw, Table2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { BrandLogo } from "@/components/brand-logo";
import { SiteDashboard } from '@/components/site-dashboard';
import { claimFirstAdmin, getMyAccess, listLeads, resyncLeadsToSheet, updateLeadStatus, leadStatuses } from "@/lib/leads.functions";

export const Route = createFileRoute("/_authenticated/painel")({
  head: () => ({
    meta: [
      { title: "Painel de contatos · Aleph Comex" },
      { name: "description", content: "Painel interno da Aleph Comex com os contatos comerciais recebidos pelo site." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Painel de contatos · Aleph Comex" },
      { property: "og:description", content: "Painel interno de contatos comerciais." },
    ],
  }),
  component: PainelPage,
});

const interestLabels: Record<string, string> = {
  consulting: "Consultoria",
  procurement: "Procurement",
  export: "Exportação / importação",
  logistics: "Logística",
};

const statusLabels: Record<string, string> = { new: "Novo", contacted: "Em contato", qualified: "Qualificado", proposal: "Proposta enviada", won: "Concluído", lost: "Encerrado" };
const aiLabels: Record<string, string> = { import: 'Importação', export: 'Exportação', plant_nutrition: 'Nutrição vegetal', animal_nutrition: 'Nutrição animal', commodities: 'Commodities', procurement: 'Procurement', consulting: 'Consultoria', logistics: 'Logística', other: 'Outros' };
const urgencyLabels: Record<string, string> = { high: 'Alta', medium: 'Média', low: 'Baixa' };

function PainelPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const access = useServerFn(getMyAccess);
  const fetchLeads = useServerFn(listLeads);
  const claim = useServerFn(claimFirstAdmin);
  const resync = useServerFn(resyncLeadsToSheet);
  const changeStatus = useServerFn(updateLeadStatus);
  const [filter, setFilter] = useState("all");
  const [term, setTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [backupFilter, setBackupFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [tab, setTab] = useState('leads');
  const [urgency, setUrgency] = useState('all');

  const accessQuery = useQuery({ queryKey: ["access"], queryFn: () => access() });
  const leadsQuery = useQuery({
    queryKey: ["leads"],
    queryFn: () => fetchLeads(),
    enabled: accessQuery.data?.isAdmin === true,
  });

  const claimMutation = useMutation({
    mutationFn: () => claim(),
    onSuccess: () => queryClient.invalidateQueries(),
  });
  const resyncMutation = useMutation({
    mutationFn: () => resync(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["leads"] }),
  });
  const statusMutation = useMutation({
    mutationFn: (data: { id: string; status: typeof leadStatuses[number] }) => changeStatus({ data }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["leads"] }),
  });

  const leads = leadsQuery.data?.leads ?? [];
  const visible = useMemo(
    () =>
      leads.filter((lead) => {
        if (urgency !== 'all' && lead.ai_urgency !== urgency) return false;
        if (filter !== "all" && lead.interest !== filter) return false;
        if (statusFilter !== "all" && lead.status !== statusFilter) return false;
        if (backupFilter === "pending" && lead.sheet_synced_at) return false;
        if (backupFilter === "synced" && !lead.sheet_synced_at) return false;
        if (fromDate && lead.created_at.slice(0, 10) < fromDate) return false;
        if (toDate && lead.created_at.slice(0, 10) > toDate) return false;
        if (!term) return true;
        const haystack = `${lead.name_role} ${lead.company_tax_id} ${lead.email} ${lead.markets} ${lead.utm_source ?? ""} ${lead.utm_campaign ?? ""}`.toLowerCase();
        return haystack.includes(term.toLowerCase());
      }),
    [leads, filter, term, statusFilter, backupFilter, fromDate, toDate, urgency],
  );

  function exportCsv() {
    const headers = ["Data", "Idioma", "Nome/cargo", "Empresa", "E-mail", "Telefone", "Interesse", "Mercados", "Mensagem", "utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid", "Origem", "Página de entrada", "fbclid", "Página do formulário", "Status", "ID", "Backup"];
    const escape = (value: unknown) => { const text = String(value ?? ""); return `"${(/^[\s]*[=+@-]/.test(text) ? "'" + text : text).replace(/"/g, '""')}"`; };
    const rows = visible.map((lead) => [lead.created_at, lead.locale, lead.name_role, lead.company_tax_id, lead.email, lead.phone, interestLabels[lead.interest] ?? lead.interest, lead.markets, lead.message, lead.utm_source, lead.utm_medium, lead.utm_campaign, lead.utm_term, lead.utm_content, lead.gclid, lead.referrer, lead.landing_page, lead.fbclid, lead.page_path, statusLabels[lead.status] ?? lead.status, lead.id, lead.sheet_synced_at].map(escape).join(","));
    const blob = new Blob([`\uFEFF${[headers.map(escape).join(","), ...rows].join("\n")}`], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `aleph-comex-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  async function signOut() {
    await queryClient.cancelQueries();
    queryClient.clear();
    await supabase.auth.signOut();
    navigate({ to: "/auth", replace: true });
  }

  if (accessQuery.isLoading) return <p className="p-10 text-sm text-muted-foreground">Carregando…</p>;
  if (accessQuery.isError) return <div className="site-container py-16"><p role="alert" className="text-destructive">Não foi possível verificar seu acesso.</p><Button className="mt-5" onClick={() => accessQuery.refetch()}>Tentar novamente</Button></div>;

  if (!accessQuery.data?.isAdmin) {
    return (
      <div className="grid min-h-dvh place-items-center bg-surface-soft px-5">
        <div className="w-full max-w-lg bg-background p-8 text-center md:p-10">
          <BrandLogo />
          <h1 className="mt-8 font-display text-2xl font-semibold">Acesso ainda não liberado</h1>
          {accessQuery.data?.adminsExist ? (
            <p className="mt-3 text-sm text-muted-foreground">Peça a um administrador da Aleph Comex para liberar sua conta no painel.</p>
          ) : (
            <>
              <p className="mt-3 text-sm text-muted-foreground">O acesso administrativo é reservado à conta confirmada da Michelle.</p>
              <Button className="mt-6" onClick={() => claimMutation.mutate()} disabled={claimMutation.isPending}>
                Liberar acesso da Michelle<ArrowRight />
              </Button>
            </>
          )}
          {claimMutation.isError && <p role="alert" className="mt-4 text-sm text-destructive">Não foi possível liberar o acesso.</p>}
          <Button variant="link" onClick={signOut} className="mt-6 w-full">Sair</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-surface-soft">
      <header className="border-b border-border bg-background">
        <div className="site-container flex flex-wrap items-center justify-between gap-4 py-5">
          <BrandLogo />
          <div className="flex flex-wrap items-center gap-2">
            {leadsQuery.data?.spreadsheetUrl && (
              <Button asChild variant="outline"><a href={leadsQuery.data.spreadsheetUrl} target="_blank" rel="noreferrer"><Table2 />Planilha de backup</a></Button>
            )}
            <Button variant="outline" onClick={() => resyncMutation.mutate()} disabled={resyncMutation.isPending}><RefreshCw />Sincronizar pendentes</Button>
            <Button variant="outline" onClick={exportCsv}><Download />CSV</Button>
            <Button variant="ghost" onClick={signOut}>Sair</Button>
          </div>
        </div>
      </header>

      <main className="site-container py-10">
        <nav aria-label="Visões do painel" className="mb-8 flex gap-3 border-b border-border pb-4"><Button variant={tab === 'leads' ? 'default' : 'ghost'} onClick={() => setTab('leads')}>Leads recebidos</Button><Button variant={tab === 'metrics' ? 'default' : 'ghost'} onClick={() => setTab('metrics')}>Métricas do site</Button></nav>
        {tab === 'metrics' ? <SiteDashboard /> : <>
        <h1 className="font-display text-3xl font-semibold">Contatos recebidos</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {leads.length} registro(s) · {visible.length} exibido(s) · Até 500 contatos mais recentes
          {resyncMutation.data ? ` · ${resyncMutation.data.synced} enviado(s) à planilha` : ""}
        </p>

        <div className="mt-8 grid grid-cols-2 gap-6 border-y border-border py-6 lg:grid-cols-4">
          {[["Novos", leads.filter(lead => lead.status === "new").length], ["Em andamento", leads.filter(lead => ["contacted", "qualified", "proposal"].includes(lead.status)).length], ["Concluídos", leads.filter(lead => lead.status === "won").length], ["Backup pendente", leads.filter(lead => !lead.sheet_synced_at).length]].map(([label, count]) => <div key={label}><p className="text-sm text-muted-foreground">{label}</p><p className="mt-2 font-display text-3xl font-semibold">{count}</p></div>)}
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          <select aria-label="Filtrar por urgência" value={urgency} onChange={e => setUrgency(e.target.value)} className="h-11 border border-input bg-background px-3"><option value="all">Todas as urgências</option>{Object.entries(urgencyLabels).map(([value, label]) => <option key={value} value={value}>Urgência {label.toLowerCase()}</option>)}</select>
          <input aria-label="Buscar contatos" maxLength={200} value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Buscar por nome, empresa, e-mail, campanha…" className="h-11 w-full min-w-0 flex-1 border border-input bg-background px-3 outline-none focus:border-brand sm:min-w-72" />
          <select aria-label="Filtrar por interesse" value={filter} onChange={(e) => setFilter(e.target.value)} className="h-11 max-w-full border border-input bg-background px-3 outline-none focus:border-brand">
            <option value="all">Todos os interesses</option>
            {Object.entries(interestLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
          <select aria-label="Filtrar por status" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="h-11 max-w-full border border-input bg-background px-3"><option value="all">Todos os status</option>{leadStatuses.map(status => <option key={status} value={status}>{statusLabels[status]}</option>)}</select>
          <select aria-label="Filtrar por backup" value={backupFilter} onChange={e => setBackupFilter(e.target.value)} className="h-11 max-w-full border border-input bg-background px-3"><option value="all">Todos os backups</option><option value="pending">Backup pendente</option><option value="synced">Backup realizado</option></select>
          <label className="flex items-center gap-2 text-sm">De<input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="h-11 min-w-0 border border-input bg-background px-3" /></label>
          <label className="flex items-center gap-2 text-sm">Até<input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="h-11 min-w-0 border border-input bg-background px-3" /></label>
          <Button variant="ghost" onClick={() => { setTerm(""); setFilter("all"); setStatusFilter("all"); setBackupFilter("all"); setFromDate(""); setToDate(""); }}>Limpar filtros</Button>
        </div>

        {statusMutation.isError && <p role="alert" className="mt-4 text-sm text-destructive">Não foi possível atualizar o status. Tente novamente.</p>}
        {statusMutation.isSuccess && <p role="status" className="mt-4 text-sm text-success">Status atualizado.</p>}
        {resyncMutation.isError && <p role="alert" className="mt-4 text-sm text-destructive">Não foi possível enviar os contatos à planilha. Os contatos continuam salvos no painel.</p>}

        {leadsQuery.isLoading && <p className="mt-8 text-sm text-muted-foreground">Carregando contatos…</p>}
        {leadsQuery.isError && <p className="mt-8 text-sm text-destructive">Não foi possível carregar os contatos.</p>}

        <div className="mt-8 grid gap-4">
          {visible.map((lead) => (
            <article key={lead.id} className="grid gap-4 rounded-sm border border-border bg-background p-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)]">
              <div>
                <p className="text-xs uppercase tracking-[.1em] text-muted-foreground">
                  {new Date(lead.created_at).toLocaleString("pt-BR")} · {lead.locale.toUpperCase()}
                </p>
                <h2 className="mt-2 break-words font-display text-xl font-semibold">{lead.name_role}</h2>
                <label className="mt-4 grid gap-2 text-xs font-semibold text-muted-foreground">Status<select aria-label={`Status de ${lead.name_role}`} value={lead.status} disabled={statusMutation.isPending} onChange={e => { const status = leadStatuses.find(item => item === e.target.value); if (status) statusMutation.mutate({ id: lead.id, status }); }} className="h-11 w-full border border-input bg-background px-3 text-sm text-foreground">{!leadStatuses.some(status => status === lead.status) && <option value={lead.status}>{lead.status}</option>}{leadStatuses.map(status => <option key={status} value={status}>{statusLabels[status]}</option>)}</select></label>
                <p className="mt-1 text-sm text-muted-foreground">{lead.company_tax_id}</p>
                <div className="mt-3 grid gap-1 text-sm">
                  <a href={`mailto:${lead.email}`} className="break-all text-brand underline">{lead.email}</a>
                  {lead.phone && <a href={`tel:${lead.phone.replace(/[^+\d]/g, "")}`}>{lead.phone}</a>}
                </div>
              </div>
              <div className="break-words text-sm">
                <div className="mb-4 border-l-2 border-brand pl-3"><p className="font-semibold">Classificação por IA</p>{lead.ai_state === 'ready' ? <><p className="mt-2">{aiLabels[lead.ai_interest ?? ''] ?? 'Outros'} · Urgência {urgencyLabels[lead.ai_urgency ?? ''] ?? '—'}</p><p className="mt-2 text-muted-foreground">{lead.ai_reason}</p></> : <p className="mt-2 text-muted-foreground">{lead.ai_state === 'failed' ? 'Classificação indisponível' : lead.ai_state === 'paused' ? 'Classificação pausada' : 'Aguardando classificação'}</p>}</div>
                <p><span className="text-muted-foreground">Interesse:</span> {interestLabels[lead.interest] ?? lead.interest}</p>
                <p className="mt-1"><span className="text-muted-foreground">Mercados:</span> {lead.markets}</p>
                {lead.message && <p className="mt-3 whitespace-pre-line text-muted-foreground">{lead.message}</p>}
              </div>
              <div className="break-all text-xs leading-6 text-muted-foreground">
                <p className="font-semibold uppercase tracking-[.1em] text-foreground">Origem</p>
                <p>utm_source: {lead.utm_source ?? "—"}</p>
                <p>utm_medium: {lead.utm_medium ?? "—"}</p>
                <p>utm_campaign: {lead.utm_campaign ?? "—"}</p>
                <p>utm_term: {lead.utm_term ?? "—"}</p>
                <p>utm_content: {lead.utm_content ?? "—"}</p>
                {lead.gclid && <p>gclid: {lead.gclid}</p>}
                {lead.fbclid && <p>fbclid: {lead.fbclid}</p>}
                <p>Página do formulário: {lead.page_path ?? "—"}</p>
                <p className="break-all">referrer: {lead.referrer ?? "—"}</p>
                <p className="break-all">entrada: {lead.landing_page ?? "—"}</p>
                <p className="mt-2">{lead.sheet_synced_at ? "Backup na planilha ✓" : "Backup pendente"}</p>
              </div>
            </article>
          ))}
          {!leadsQuery.isLoading && visible.length === 0 && (
            <p className="text-sm text-muted-foreground">Nenhum contato encontrado com os filtros atuais.</p>
          )}
        </div>
        </>}
      </main>
    </div>
  );
}
